// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — shared projection helpers (PR-B extraction)
// ═══════════════════════════════════════════════════════════════════════════
//
// The ONE-TRUTH pagination / sort-parse / fullField-single-field-mode / field-projection
// primitives EVERY route body (`projectEntities`, `projectLayers`, `projectBlocks`,
// `projectSpaces`) reuses. Extracted from PR-A's `projectEntities.ts` so the 4 bodies do
// NOT clone the same slice/sort/project logic (dup:check one-truth — a divergent paginate
// or fullField rule would drift the page shape between routes). PR-A's body is refactored
// to import these (no behaviour change — its matrix tests still pass).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam types (`DecodedProjectionCursor`,
// `StateReadQuery`) + the shared `clampLimit`/`cursorOffset` clamps — NO editorStore /
// agentBridge / command-engine. `npm run deps:graph` (depcruise) cycles=0.
//
// THE #1 LESSON (PR-A round-2 P2): sort grammar is the CANONICAL EXPLICIT `key:dir` ONLY
// (a BARE `?sort=name` is rejected as `unknown-sort` at the seam BEFORE the body — the
// bare→`:asc` alias was REMOVED because the companion's `deriveQueryShape` cursor-binds the
// RAW `sortOrder` string, so a browser-side normalization seam would false-stale a
// continuation that switched spelling as `409 query-shape-changed`). So `parseKeyDirSort`
// here splits a well-formed `key:dir` ONLY — no alias expansion.
// ═══════════════════════════════════════════════════════════════════════════

import {
  clampLimit,
  cursorOffset,
  type DecodedProjectionCursor,
  type EntityBoundsExtent,
  type SceneSnapshotRef,
  type StateReadPage,
  type StateReadQuery,
} from "../sceneSummaryProjector";

/** The sort direction parsed from the canonical `key:dir` grammar (`asc` is the explicit default). */
export type SortDir = "asc" | "desc";

/**
 * Parse the canonical `key:dir` sort into its `{ key, dir }`, narrowed to the route's legal sort
 * KEYS (`legalKeys`). The seam already validated the FULL value against the route's sort allowlist
 * (only the canonical explicit `key:dir` forms — a bare `?sort=name` is `unknown-sort`, the
 * bare→`:asc` alias having been REMOVED, PR-A round-2 P2). So a sort that reaches here is
 * well-formed — but parse defensively: `undefined`/empty → null (the snapshot's natural order); an
 * unexpected key (not in `legalKeys`) → null (unreachable post-validation — defensive). The `dir`
 * is `desc` ONLY for the exact `:desc` suffix, else `asc` (the explicit default).
 */
export function parseKeyDirSort<K extends string>(
  sort: string | undefined,
  legalKeys: readonly K[],
): { key: K; dir: SortDir } | null {
  if (sort === undefined || sort.length === 0) {
    return null; // no `?sort` → the snapshot's natural (stable) order.
  }
  const [rawKey, rawDir] = sort.split(":");
  if (!(legalKeys as readonly string[]).includes(rawKey)) {
    return null; // unreachable post-validation (the seam rejected an unknown key) — defensive.
  }
  return { key: rawKey as K, dir: rawDir === "desc" ? "desc" : "asc" };
}

/**
 * A comparator over two facets for a `{ key, dir }` sort, comparing the value the
 * `valueOf(facet, key)` extractor returns. Supports STRING and NUMBER key values (a layer's
 * `lineweight`/`color` is a number, a name is a string) — the `<` comparison orders both
 * correctly; a value-type the extractor cannot order (undefined on both) ties. `desc` negates the
 * PRIMARY comparison.
 *
 * TIE-BREAK (PR-B @codex round-3, findings #1/#2): the documented contract for the document-level
 * TABLE routes is DESC `<primary>` → **`name` ASC tie-break** (fullstack:963 `layers?sort=count` →
 * DESC entityCount → name ASC; :964 `blocks?sort=count` → DESC referenceCount → name ASC). A bare
 * stable-tie (return 0 on equal primary keys) preserves DXF TABLE order, which is NOT the documented
 * `name ASC` final order AND mis-paginates a continuation (the cursor offset indexes a different
 * order than a client expects). So an OPTIONAL `tieBreakKey` gives the comparator a DETERMINISTIC
 * FINAL tie-break: when the primary values are equal, compare each tie-break key value ASC IN ORDER
 * (ALWAYS ascending — the contract names `name ASC` regardless of the primary `dir`; the tie-break is
 * a stable total order, not a mirror of the primary direction). `tieBreakKey` accepts a SINGLE key OR
 * an ORDERED ARRAY of keys (PR-C): the table routes pass `'name'` (one key) so EVERY sort on those
 * routes (count, color, lineweight, type — not only count) gets the contract's `name ASC` final order;
 * the `viewports` route passes the ORDERED chain `['spaceId','viewportId','id']` so an explicit sort
 * (e.g. `viewportId:desc`) still resolves equal-primary ties by the deterministic `(spaceId,
 * viewportId, id)` chain (the key already used as the primary is SKIPPED in the chain to avoid a
 * redundant compare). `projectEntities` passes NO tieBreakKey (an entity has no unique key), keeping
 * its snapshot-order stable tie (ES2019+ `Array.sort` stable). When `tieBreakKey` is absent OR every
 * tie-break key also ties, returns 0 (the stable snapshot-order tie).
 */
export function compareFacetsBy<F, K extends string>(
  a: F,
  b: F,
  key: K,
  dir: SortDir,
  valueOf: (facet: F, key: K) => string | number | undefined,
  tieBreakKey?: K | readonly K[],
): number {
  const primary = compareValues(valueOf(a, key), valueOf(b, key), dir);
  if (primary !== 0) {
    return primary;
  }
  // PRIMARY tie → the deterministic FINAL tie-break chain (ALWAYS ASC, never mirrored to `dir`): the
  // documented `name ASC` final order for the table routes, or the `(spaceId,viewportId,id)` chain for
  // the viewports route. Absent on `projectEntities` → 0 (the stable snapshot-order tie). The primary
  // `key` is skipped in the chain (a redundant compare always ties here).
  if (tieBreakKey !== undefined) {
    const chain = Array.isArray(tieBreakKey) ? tieBreakKey : [tieBreakKey as K];
    for (const tk of chain) {
      if (tk === key) {
        continue; // the primary key already tied — skip a redundant compare.
      }
      const tie = compareValues(valueOf(a, tk), valueOf(b, tk), "asc");
      if (tie !== 0) {
        return tie;
      }
    }
  }
  return 0; // stable tie — preserve snapshot order (no tie-break key, or every tie-break also ties).
}

/**
 * Compare two extracted facet values (string | number | undefined) for a `dir` direction. The ONE
 * comparison primitive `compareFacetsBy` reuses for BOTH the primary key and the tie-break key so
 * the ordering rules (absent-sorts-first, total order, `desc` negation) are one truth. An absent
 * value sorts BEFORE a present one (deterministic; `undefined < x` is false in JS so handle it
 * explicitly to keep a total order). Equal values → 0.
 */
function compareValues(
  av: string | number | undefined,
  bv: string | number | undefined,
  dir: SortDir,
): number {
  if (av === bv) {
    return 0;
  }
  if (av === undefined) {
    return dir === "desc" ? 1 : -1;
  }
  if (bv === undefined) {
    return dir === "desc" ? -1 : 1;
  }
  const cmp = av < bv ? -1 : 1;
  return dir === "desc" ? -cmp : cmp;
}

/**
 * Project a facet to EXACTLY the requested `fields` (a record read by allowlisted key). INV-1 — the
 * caller (the seam's `validateRouteQuery`) already rejected any field ∉ the route's allowlist, so
 * every key here is an allowlisted facet member. An OPTIONAL field absent on the facet is simply
 * NOT emitted (the key is omitted, not set to `undefined`) — mirroring AutoCAD's "the property is
 * not set" vs "set to a value" distinction (a `referenceCount` absent unless `includeReferenceCount`,
 * an `entityBounds` absent when unknown).
 */
export function projectFacetFields(
  facet: Record<string, unknown>,
  fields: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const value = facet[f];
    if (value !== undefined) {
      out[f] = value;
    }
  }
  return out;
}

/**
 * The resolved projection mode + page window for a route body: the EFFECTIVE `?fullField` (null
 * unless a non-empty value is present), the effective `limit` (FORCED to 1 in fullField mode,
 * overriding `?limit` — the single-field UNCAPPED page; else the `clampLimit`-clamped `?limit`), and
 * the resolved `fields` set (ONLY `[fullField]` in fullField mode — it takes PRECEDENCE over
 * `?fields`/the lean default; else `?fields` if present, else the supplied `leanDefault`).
 *
 * `?fullField` (stateRoute.ts:98-102 / fullstack:967 — "single-field UNCAPPED full-value retrieval,
 * forces `limit=1`") is the SAME contract across EVERY paged route, so resolving it ONCE here keeps
 * the 4 bodies identical (dup:check one-truth). The seam already validated `?fullField` names an
 * allowlisted field (mirrors `?fields`), so it is safe to project.
 */
export function resolveProjectionMode(
  query: StateReadQuery,
  leanDefault: readonly string[],
): { fullField: string | null; limit: number; fields: readonly string[] } {
  const fullField =
    query.fullField !== undefined && query.fullField.length > 0 ? query.fullField : null;
  const limit = fullField !== null ? 1 : clampLimit(query.limit);
  const fields = fullField !== null ? [fullField] : (query.fields ?? leanDefault);
  return { fullField, limit, fields };
}

// ── WCS bounds union (the one-truth `entityBounds` union — shared by spaces + capture) ──

/**
 * Union two WCS extents into one (min of mins, max of maxes on X/Y). The Z extent is unioned ONLY over
 * the operands that actually carry one — a flat 2D operand contributes no Z, so a 2D∪2D union omits Z
 * entirely while a 2D∪3D union keeps the 3D operand's Z verbatim. Avoids fabricating a `0` Z for a flat
 * entity (which would wrongly widen a real 3D extent toward 0).
 *
 * The ONE TRUTH for unioning `entityBounds`: the `spaces` route body (`projectSpaces`) unions a space's
 * entity bounds for the DERIVED `entityBounds` facet, and the S7 capture closure (`state/captureSnapshot`)
 * pre-computes the SAME per-space union for a self-consistent snapshot. A divergent union rule would drift
 * the captured bound from the projected one (dup:check one-truth — extract, don't clone).
 */
export function unionEntityBounds(a: EntityBoundsExtent, b: EntityBoundsExtent): EntityBoundsExtent {
  const out: EntityBoundsExtent = {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
  // Collect the Z mins/maxes that are actually present (a 2D operand contributes none).
  const minZs = [a.minZ, b.minZ].filter((z): z is number => z !== undefined);
  const maxZs = [a.maxZ, b.maxZ].filter((z): z is number => z !== undefined);
  if (minZs.length === 0 && maxZs.length === 0) {
    return out; // both flat → no Z extent.
  }
  return {
    ...out,
    ...(minZs.length > 0 ? { minZ: Math.min(...minZs) } : {}),
    ...(maxZs.length > 0 ? { maxZ: Math.max(...maxZs) } : {}),
  };
}

// ── ordered-count array (`OrderedTypeCount`/`buildOrderedTypeCounts`) — MOVED to the seam ──────
// The summary `byType`/`byLayer`/blocks `rawChildByType` shared ordered-count builder now lives
// in `../sceneSummaryProjector` (the seam OWNS `byType`/`byLayer` via `projectSummary`) — keeping
// it here would form an import CYCLE the moment the seam needed it back (this file already
// imports `clampLimit`/`cursorOffset` FROM the seam, an established one-way edge). Nothing in
// THIS file consumes `OrderedTypeCount`/`buildOrderedTypeCounts`/`RAW_CHILD_BY_TYPE_CAP` anymore —
// `state/captureSnapshot.ts` (the blocks route's S7 capture closure) imports them directly from
// `../sceneSummaryProjector`, alongside the other seam facet types it already imports from there.

/** The offset-key page slice of an already-ordered facet list (the SHARED paginate primitive). */
export interface PageSlice<F> {
  /** The facets on this page (the `[offset, offset+limit)` window). */
  readonly slice: readonly F[];
  /** The next offsetKey (a number) when more remain, else `null` (the last page). */
  readonly nextOffsetKey: number | null;
}

/**
 * Slice a page from an ALREADY-ORDERED facet list using the decoded cursor's offset (the SHARED seam
 * `cursorOffset` decode) + the resolved `limit`. Returns the page window + the `nextOffsetKey` (a
 * number when more remain, `null` on the last page). The ONE paginate primitive every route body
 * reuses so a continuation pages identically (the offset decode + the `hasMore` test are one truth).
 */
export function paginate<F>(
  ordered: readonly F[],
  cursor: DecodedProjectionCursor | null,
  limit: number,
): PageSlice<F> {
  const offset = cursorOffset(cursor);
  const slice = ordered.slice(offset, offset + limit);
  const nextOffset = offset + slice.length;
  const hasMore = nextOffset < ordered.length;
  return { slice, nextOffsetKey: hasMore ? nextOffset : null };
}

// ── finishPage — the SHARED route-body tail (projection-mode → paginate → field-project → envelope) ──

/** The snapshot anchor fields every route body stamps onto its page (the `[L7]` response metadata). */
export interface PageAnchors {
  /** The route-SPECIFIC binding tuple the body computed (NEVER `snapshotRevision` — INV-9). */
  readonly routeRevisions: Readonly<Record<string, number>>;
  /** The resolved space id (a space-scoped route's space, or `null` for document-level / nested). */
  readonly resolvedSpaceId: string | null;
  /** The coarse `snapshotRevision` RESPONSE ANCHOR (NEVER a cursor-binding key, [L7]). */
  readonly snapshotRevision: number;
  /** The browser reader column-schema version carried in the cursor. */
  readonly projectionVersion: number;
}

/**
 * The SHARED route-body TAIL every paged projector (`entities`/`layers`/`blocks`/`spaces`/`viewports`/
 * the two viewport nested routes) runs AFTER it has produced its ORDERED facet list: resolve the
 * projection-mode + limit (`resolveProjectionMode` — `?fullField` forces limit=1, else `?fields`/the
 * lean default), paginate (the SHARED `paginate`), field-project each facet (`projectFacetFields` —
 * INV-1, an absent optional omitted), and assemble the canonical `StateReadPage` envelope with the
 * caller's route-specific `routeRevisions` + the response anchors. EXTRACTED (dup:check one-truth) so
 * the identical tail does NOT clone across the 7 bodies — a body now reduces to `scope/enumerate →
 * sort → finishPage(...)`. `byteTruncated` is ALWAYS `false` here (the companion serializer owns
 * byte-budget truncation against the per-request frame budget — never the projector).
 *
 * The facet `F` is mapped to a `Record<string, unknown>` view for `projectFacetFields`; a body passes
 * its already-ordered rows (each a facet whose allowlisted keys are projected). `total` is the FULL
 * ordered length (NOT the page slice) so the agent sees the unpaginated count.
 */
export function finishPage<F>(
  ordered: readonly F[],
  query: StateReadQuery,
  cursor: DecodedProjectionCursor | null,
  leanDefault: readonly string[],
  anchors: PageAnchors,
): StateReadPage {
  const { fields, limit } = resolveProjectionMode(query, leanDefault);
  const { slice, nextOffsetKey } = paginate(ordered, cursor, limit);
  const items = slice.map((row) =>
    projectFacetFields(row as unknown as Record<string, unknown>, fields),
  );
  return {
    items,
    nextOffsetKey,
    routeRevisions: anchors.routeRevisions,
    resolvedSpaceId: anchors.resolvedSpaceId,
    snapshotRevision: anchors.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false, // the companion serializer owns byte-budget truncation.
    projectionVersion: anchors.projectionVersion,
    total: ordered.length,
  };
}

/**
 * The SHARED tail for the two NESTED viewport drill-down routes (`viewport-frozen-layers` /
 * `viewport-layer-overrides`): both bind the IDENTICAL `{ geometryRevision, tableRevision }` tuple
 * (a per-VP freeze/override is ViewportEntity entity-state referencing the layer table —
 * fullstack:965) and report `resolvedSpaceId: null` (a nested drill-down is keyed by the viewport,
 * NOT a space). EXTRACTED (dup:check one-truth — the two bodies are structurally identical past their
 * row-shaping) so the binding contract is ONE truth across the pair; a change to either route's binding
 * is impossible without changing both. `snapshotRevision`/`projectionVersion` ride from the snapshot as
 * the response anchors (never cursor-binding keys, [L7]).
 */
export function finishNestedViewportPage<F>(
  ordered: readonly F[],
  query: StateReadQuery,
  cursor: DecodedProjectionCursor | null,
  leanDefault: readonly string[],
  snapshot: SceneSnapshotRef,
): StateReadPage {
  return finishPage(ordered, query, cursor, leanDefault, {
    routeRevisions: {
      geometryRevision: snapshot.revisions.geometryRevision,
      tableRevision: snapshot.revisions.tableRevision,
    },
    resolvedSpaceId: null, // a nested viewport drill-down is keyed by the viewport, not a space.
    snapshotRevision: snapshot.snapshotRevision,
    projectionVersion: snapshot.projectionVersion,
  });
}
